/**
 * Box 2 (spec §4.2): the version's weekly outcome, actual (imported) solid beside predicted (the model) dashed, styled
 * with the /looker chart theme as D1–D4's charts are. A held-out week (plan R9) has actual null, and the line leaves
 * a gap there: it never draws the week's partial sum, and never 0 (Review Focus 1). A week left alone between gaps is a
 * dot. A count outcome's title names its units, and its figures print as counts (spec C11).
 *
 * @module components/budget/ActualVsPredicted
 */
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Dot, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, type DotItemDotProps } from 'recharts';
import { colorAt } from '@/components/charts/chartPalette';
import { AXIS_PROPS, CHART_HEIGHT, GRID_PROPS, LEGEND_PROPS, TOOLTIP_PROPS, categoryTick, compactTick } from '@/components/looker/charts/chartTheme';
import { OverviewCard } from '@/components/overview/OverviewBox';
import type { OutcomeMetric } from '@/services/budget/budgetContract';
import type { Bundle } from '@/services/budget/bundleSchema';
import { isCount, outcomeFigure, unitsName } from '@/services/budget/outcomeUnits';

/**
 * Slots past the four channels' (AD_PLATFORMS), so neither line shares a colour with a channel in boxes 3 and 5
 * beside it. The pair passes the dataviz validator (2026-10-29).
 */
const ACTUAL_COLOR = colorAt(4);
const PREDICTED_COLOR = colorAt(6);

/** Held out (plan R9), or past either end of the window. */
const heldOut = (weekly: Bundle['weekly'], i: number): boolean => (weekly[i]?.actual ?? null) === null;

interface Props {
  weekly: Bundle['weekly'];
  /** The version's currency (bundle.currency), which a revenue version's figures are in. */
  currency: string;
  /** The version's outcome (bundle.outcome_metric). */
  metric: OutcomeMetric;
}

export const ActualVsPredicted: React.FC<Props> = ({ weekly, currency, metric }) => {
  const { t } = useTranslation();
  const figure = outcomeFigure(metric, currency);
  const value = (v: unknown): string => figure(typeof v === 'number' ? v : null);
  const title = isCount(metric) ? t('budget.actual.titleCount', { units: unitsName(metric, t) }) : t('budget.actual.title');
  // A complete week whose neighbours are both held out is a one-point segment, which a line strokes as nothing.
  const loneWeekDot = ({ index, cx, cy }: Pick<DotItemDotProps, 'index' | 'cx' | 'cy'>): ReactNode =>
    (!heldOut(weekly, index) && heldOut(weekly, index - 1) && heldOut(weekly, index + 1)
      ? <Dot cx={cx} cy={cy} r={2} fill={ACTUAL_COLOR} />
      : null);
  return (
    <OverviewCard id="budget-2" title={title}>
      <p className="-mt-2 mb-2 text-xs text-neutral-700">{t('budget.actual.subtitle', { n: weekly.length })}</p>
      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={weekly} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="week" {...AXIS_PROPS} tickFormatter={categoryTick(t)} />
            <YAxis {...AXIS_PROPS} tickFormatter={compactTick} />
            <Tooltip {...TOOLTIP_PROPS} labelFormatter={categoryTick(t)} formatter={value} />
            <Legend {...LEGEND_PROPS} />
            <Line
              dataKey="actual" name={t('budget.actual.actual')} stroke={ACTUAL_COLOR} strokeWidth={2} dot={loneWeekDot} connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              dataKey="predicted" name={t('budget.actual.predicted')} stroke={PREDICTED_COLOR} strokeWidth={2} strokeDasharray="5 4"
              dot={false} isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-2 text-xs text-neutral-700">{t('budget.actual.how')}</p>
    </OverviewCard>
  );
};
